import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Eye, Crown, Calendar, NotePencil } from '@phosphor-icons/react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ProfileAvatar, ProfileStatus } from '../components/ProfileIdentity';
import { usernameColorClass } from '../lib/usernameColors';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProfilePage() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setProfile(null);
    let cancelled = false;
    axios.get(`${API}/users/${encodeURIComponent(username)}`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setProfile(data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [username]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6" data-testid="public-profile-missing">
        <p className="text-lg text-muted-foreground">This profile is not available.</p>
        <Link to="/members"><Button variant="outline" className="rounded-full">Back to members</Button></Link>
      </div>
    );
  }

  if (!profile) return <div className="min-h-screen" />;

  return (
    <div className="min-h-screen py-10 md:py-14 px-4 sm:px-6" data-testid="public-profile-page">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <Card className="p-6">
          <ProfileAvatar src={profile.avatar_url} username={profile.username} testId="public-profile-avatar" className="h-24 w-24 mb-5" />
          <h1 className={`text-3xl sm:text-4xl font-light tracking-tight ${usernameColorClass(profile.username_color)}`} data-testid="public-profile-username">
            {profile.username}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5" data-testid="public-profile-views">
              <Eye size={14} /> {profile.profile_views} profile {profile.profile_views === 1 ? 'visit' : 'visits'}
            </span>
            <span className="flex items-center gap-1.5">
              <NotePencil size={14} /> {profile.thoughts_posted} shared
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} /> Joined {new Date(profile.member_since).toLocaleDateString()}
            </span>
            {profile.membership_level > 0 && (
              <span className="flex items-center gap-1.5 text-amber-300" data-testid="public-profile-elite">
                <Crown size={14} weight="fill" /> Elite Level {profile.membership_level}
              </span>
            )}
          </div>
          {profile.bio && <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words mt-5" data-testid="public-profile-bio">{profile.bio}</p>}
          <ProfileStatus status={profile.status} expiresAt={profile.status_expires_at} testId="public-profile-status" />
        </Card>

        <div className="space-y-4">
          <h2 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Shared thoughts</h2>
          {profile.thoughts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing shared publicly yet.</p>
          ) : (
            profile.thoughts.map((t) => (
              <Card key={t.id} className="p-5" data-testid={`public-thought-${t.id}`}>
                <h3 className="text-lg font-light mb-1">{t.title}</h3>
                <p className="text-sm text-muted-foreground">{t.content}</p>
                <p className="text-xs text-muted-foreground mt-3">
                  {t.likes} likes · {t.comments_count} comments · {new Date(t.created_at).toLocaleDateString()}
                </p>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
